import { useMemo, useState } from "react";
import { Chip, Flex, Dropdown, DropdownItem, Icon } from "@goodfill/ui-components";
import { BondSortDirection } from "./sortBondList";
import { SortingIconASC, SortingIconDSC } from "./assets/SortingIcon";

const sortKeys = [
  "offerYield",
  "price",
  "maturity",
  "g-spread",
  "ticker",
  "issuer",
  "rating-sp",
  "rating-moody",
  "amount",
  "payment-rank",
  "coupon",
] as const;

export type SortKey = typeof sortKeys[number];

const labelOf: Record<SortKey, string> = {
  issuer: "Issuer",
  ticker: "Ticker",
  coupon: "Coupon",
  maturity: "Maturity",
  "g-spread": "G-Spread",
  offerYield: "Offer Yield",
  price: "Price",
  "rating-moody": "Rating Moody",
  "rating-sp": "Rating S&P",
  amount: "Minimum Amount",
  "payment-rank": "Payment Rank",
};

const defaultSortingDirection: Record<SortKey, BondSortDirection> = {
  issuer: "ASC",
  price: "ASC",
  ticker: "ASC",
  amount: "ASC",
  offerYield: "DESC",
  coupon: "ASC",
  maturity: "ASC",
  "g-spread": "ASC",
  "rating-moody": "ASC",
  "rating-sp": "ASC",
  "payment-rank": "ASC",
};

export interface SortingDropdownProps {
  selectedSortKey: SortKey;
  selectedSortDirection: BondSortDirection;
  setSelectedSortKey: (value: SortKey) => void;
  setSelectedSortDirection: (value: BondSortDirection) => void;
}

export const SortingDropdown: React.FC<SortingDropdownProps> = ({
  selectedSortDirection,
  selectedSortKey,
  setSelectedSortDirection,
  setSelectedSortKey,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const items = useMemo(
    () =>
      sortKeys.map((sortKey) => ({
        key: sortKey,
        selected: sortKey === selectedSortKey,
        label: labelOf[sortKey],
        onClick: () => {
          if (sortKey === selectedSortKey) {
            setSelectedSortDirection(selectedSortDirection === "ASC" ? "DESC" : "ASC");
          } else {
            setSelectedSortKey(sortKey);
            setSelectedSortDirection(defaultSortingDirection[sortKey]);
          }
          setIsOpen(false);
        },
      })),
    [selectedSortKey, setSelectedSortKey, setSelectedSortDirection, selectedSortDirection, sortKeys],
  );

  return (
    <Dropdown
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      right={true}
      trigger={
        <Chip size="medium" className="p-0-important">
          <Flex direction="row" gap={2} items="center" className="text-[14px]">
            {selectedSortDirection === "ASC" ? (
              <>
                <SortingIconASC />
              </>
            ) : (
              <>
                <SortingIconDSC />
              </>
            )}
            &nbsp;
            {labelOf[selectedSortKey]}
            <Icon name={isOpen ? "caret-up" : "caret-down"} size={12} />
          </Flex>
        </Chip>
      }
    >
      {items.map(({ key, label, onClick }) => (
        <DropdownItem key={key} onClick={onClick}>
          &nbsp;
          {label}
        </DropdownItem>
      ))}
    </Dropdown>
  );
};
